import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { Membership, SupportSession, User } from '@/types/api';
import { primaryRole } from '@/lib/roles';

export type UserRole = 'admin' | 'teacher' | 'student' | 'parent' | 'coordinator';

/**
 * The "no offices" answer, shared rather than built per call.
 *
 * `getEffectiveRoles` is read through a zustand selector, which compares
 * snapshots by identity: a fresh `[]` on every call is a new snapshot every
 * render, which is how a selector turns into a render loop.
 */
const NO_ROLES: UserRole[] = [];

interface ImpersonatedUser {
  id: string;
  firstName: string;
  lastName: string;
  email: string | null;
}

interface ImpersonationState {
  isImpersonating: boolean;
  /**
   * The offices the session is *acting as*. A list to match `user.roles`, though
   * impersonation only ever puts one in it: the point is to see the app as one
   * audience, and handing over everything the target holds would show the admin
   * more than that person sees.
   */
  effectiveRoles: UserRole[];
  actualRoles: UserRole[];
  impersonatedUser?: ImpersonatedUser;
}

interface AuthState {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  /**
   * The other schools this person belongs to, once the app has asked.
   *
   * Not part of the session and not what scopes a request - `user.schoolId` is
   * the single answer to "which school am I in", and `lib/api.ts` builds
   * `X-School-ID` from it. This list only decides whether a switcher is offered
   * and what it contains, and is empty until `/auth/memberships` is fetched.
   */
  memberships: Membership[];
  // Impersonation state
  impersonation: ImpersonationState | null;
  /**
   * Set when a platform operator is driving this session through WorkOS support
   * access, and null otherwise. See `SupportSession`.
   *
   * Reported by `GET /api/auth/me` and by nothing else the client can ask, so it
   * is captured at sign-in and then carried: the token keeps the claim across
   * switching school and impersonating, and this mirrors it so the banner does
   * not blink out during either.
   */
  support: SupportSession | null;
  login: (user: User, token: string, support?: SupportSession | null) => void;
  logout: () => void;
  setMemberships: (memberships: Membership[]) => void;
  /**
   * Adopt the session the API minted for another school.
   *
   * The user and the token move together, deliberately: they disagree for as
   * long as it takes to set them separately, and during that window every
   * request would carry one school's token under another school's header and be
   * refused with a 403.
   */
  switchSchool: (user: User, token: string) => void;
  /** Whether to offer a switcher at all. */
  hasOtherSchools: () => boolean;
  // Impersonation actions
  startImpersonation: (token: string, impersonatingRoles: UserRole[], impersonatedUser: ImpersonatedUser) => void;
  stopImpersonation: (token: string) => void;
  /**
   * The offices this request is acting with - the impersonated ones while
   * impersonating, otherwise the person's own.
   *
   * Ask this, not `user.roles`, wherever the question is "may they" or "what
   * should this look like": an admin looking through a student's eyes has to be
   * treated as that student, which is the whole point of impersonation.
   */
  getEffectiveRoles: () => UserRole[];
  /**
   * The single office that shapes the UI, from the set above.
   *
   * For *display*: which dashboard to land on, which nav to draw, which label to
   * print. Never for "may they" - that is `getEffectiveRoles().includes(...)`,
   * because a teacher who is also a parent must keep every parent page they had,
   * and this returns only the teacher half.
   */
  getEffectiveRole: () => UserRole | null;
  // Helper to get impersonated user info
  getImpersonatedUser: () => ImpersonatedUser | null;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      isAuthenticated: false,
      memberships: [],
      impersonation: null,
      support: null,
      login: (user, token, support = null) => {
        // Memberships are cleared, not kept: this may be a different person
        // signing in, and inheriting the last one's school list would offer a
        // switcher into schools this session has no token for.
        //
        // `support` defaults to null for the same reason: an ordinary sign-in
        // that inherited the last session's banner would tell an operator they
        // were still holding somebody's account after they had signed out of it.
        set({
          user,
          token,
          isAuthenticated: true,
          impersonation: null,
          memberships: [],
          support,
        });
      },
      logout: () => {
        set({
          user: null,
          token: null,
          isAuthenticated: false,
          impersonation: null,
          memberships: [],
          support: null,
        });
      },
      setMemberships: (memberships) => {
        set({ memberships });
      },
      switchSchool: (user, token) => {
        // Impersonation ends here because the API refuses to switch while it is
        // in progress; keeping the banner would show a state the server has
        // already rejected. The membership list survives - it describes the
        // person, and they still belong to the same schools.
        set({ user, token, isAuthenticated: true, impersonation: null });
      },
      hasOtherSchools: () => get().memberships.length > 1,
      startImpersonation: (token, impersonatingRoles, impersonatedUser) => {
        const { user } = get();
        if (!user?.roles?.includes('admin')) return;

        set({
          token,
          impersonation: {
            isImpersonating: true,
            effectiveRoles: impersonatingRoles,
            actualRoles: user.roles,
            impersonatedUser,
          },
        });
      },
      stopImpersonation: (token) => {
        set({
          token,
          impersonation: null,
        });
      },
      getEffectiveRoles: () => {
        const { user, impersonation } = get();
        if (!user) return NO_ROLES;
        return impersonation?.effectiveRoles ?? user.roles ?? NO_ROLES;
      },
      getEffectiveRole: () => primaryRole(get().getEffectiveRoles()) ?? null,
      getImpersonatedUser: () => {
        const { impersonation } = get();
        return impersonation?.impersonatedUser ?? null;
      },
    }),
    {
      name: 'auth-storage',
      // Bumped by the people model. Everything persisted before it stored a
      // scalar `user.role`, and every reader here now calls `user.roles.includes`
      // - which throws on the old shape rather than degrading, so a deploy would
      // have met each already-signed-in person with a blank page until they
      // cleared site data. The old session is dropped rather than translated:
      // the persisted token carries the old `role` claim too and the API would
      // read no roles off it, so it has to be re-issued by signing in again.
      version: 2,
      migrate: (persisted, version) => {
        type Persisted = Pick<
          AuthState,
          'user' | 'token' | 'isAuthenticated' | 'impersonation' | 'memberships' | 'support'
        >;
        if (version >= 2) return persisted as Persisted;
        return {
          user: null,
          token: null,
          isAuthenticated: false,
          impersonation: null,
          memberships: [],
          support: null,
        };
      },
      partialize: (state) => ({
        user: state.user,
        token: state.token,
        isAuthenticated: state.isAuthenticated,
        impersonation: state.impersonation,
        // Persisted so a reload doesn't make the switcher disappear and come
        // back. It is a cache, not an authority: the list is re-fetched on
        // mount, and switching is re-checked server-side against the identity,
        // so a stale entry costs a 403 rather than access.
        memberships: state.memberships,
        // Persisted with the token it describes. A reload that kept the session
        // and lost the banner is the one state this feature must never reach -
        // the operator would go on acting as somebody else with nothing on
        // screen saying so.
        support: state.support,
      }),
    }
  )
);
